import React from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import EventHeader from '../EventHeader';
import Player from '../Player';
import SearchBar from '../SearchBar';
import FloatingDock from '../player/FloatingDock';
import ParticipantsList from '../ParticipantsList';
import SidePanel from '../SidePanel';
import LegacyPartyMood from './vibe/LegacyPartyMood';
import PartyVibeExperience, { PartyCrowdStage, PartyMyVibePanel, PartyVibeGate } from './vibe/PartyVibeExperience';
import { isVibeParty } from '../../vibe/legacy';
import useEventStore from '../../stores/eventStore';
import useQueueStore from '../../stores/queueStore';
import usePlayerStore from '../../stores/playerStore';
import { useRoleStore } from '../../stores/roleStore';
import { isEventHost, canResumeEvent } from '../../utils/identity';
import { normalizeEventCode } from '../../utils/eventCode';
import { RESUME_STATUS, resumeEventByCode, reconcileHostParticipantDisplay } from '../../services/eventLifecycle';
import { rememberSessionMembership } from '../../services/authSession';
import { cleanupStores } from '../../utils/storeUtils';
import { isEventEnded, isEventLive } from '../../utils/eventStatus';
import { fetchQueueSnapshot } from '../../services/sessionSync';
import EndedSessionView from './EndedSessionView';
import useQualifiedListenTracker from '../../hooks/useQualifiedListenTracker';

/**
 * The active party view for `/party/{eventCode}`.
 *
 * The route decides which party this tab shows. Two tabs can therefore sit in
 * two different parties under one account, and refreshing either one restores
 * the party in its own URL rather than whichever party was entered last.
 *
 * Arriving here does not join anything: a visitor who is not already the host
 * or a participant is sent to /join/{code} to join deliberately.
 *
 * Playback, the host-only Player mount, the dock, the side panel and the
 * search -> queue path are unchanged.
 */
export default function PartyScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeCode = normalizeEventCode(params.eventCode || '');
  const [arrivalInvite] = React.useState(() => (
    location.state?.inviteAfterCreate === true
    && normalizeEventCode(location.state?.eventId || '') === routeCode
  ));

  const eventData = useEventStore(state => state.eventData);
  const loadedEventId = useEventStore(state => state.eventData?.id ?? null);
  const addToQueue = useQueueStore(state => state.addToQueue);
  const queue = useQueueStore(state => state.queue);
  const isPlaying = usePlayerStore(state => state.isPlaying);
  const currentSong = usePlayerStore(state => state.currentSong);
  const setCurrentSong = usePlayerStore(state => state.setCurrentSong);
  const userId = useRoleStore(state => state.userId);
  const hostId = useRoleStore(state => state.hostId);
  const [sidePanelOpen, setSidePanelOpen] = React.useState(false);
  const [historyQueue, setHistoryQueue] = React.useState([]);
  const [status, setStatus] = React.useState(
    () => (loadedEventId && loadedEventId === routeCode ? RESUME_STATUS.ok : 'loading'),
  );
  const hadThisEventRef = React.useRef(loadedEventId === routeCode);
  useQualifiedListenTracker({ enabled: Boolean(loadedEventId && loadedEventId === routeCode) });
  // Snapshot whether THIS route already pointed at an ended event (history)
  // vs a live room that later flipped to ended (in-session notice).
  const arrivedEndedRef = React.useRef({ code: null, ended: undefined });
  if (eventData?.id === routeCode && arrivedEndedRef.current.code !== routeCode) {
    arrivedEndedRef.current = { code: routeCode, ended: isEventEnded(eventData) };
  }

  React.useEffect(() => {
    if (!arrivalInvite || location.state?.inviteAfterCreate !== true) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [arrivalInvite, location.pathname, location.state, navigate]);

  React.useEffect(() => {
    let cancelled = false;

    if (!routeCode) {
      setStatus(RESUME_STATUS.invalidCode);
      return undefined;
    }

    if (loadedEventId === routeCode) {
      hadThisEventRef.current = true;
      setStatus(RESUME_STATUS.ok);
      return undefined;
    }

    if (!loadedEventId && hadThisEventRef.current) {
      return undefined;
    }

    hadThisEventRef.current = false;
    setStatus('loading');
    resumeEventByCode(routeCode)
      .then((result) => {
        if (!cancelled) setStatus(result.status);
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [routeCode, loadedEventId]);

  React.useEffect(() => {
    if (!eventData || !isEventEnded(eventData) || !canResumeEvent(userId, eventData)) {
      return undefined;
    }
    const role = isEventHost(userId, hostId) ? 'host' : 'guest';
    rememberSessionMembership(eventData, role).catch(() => {});
    return undefined;
  }, [eventData, userId, hostId]);

  React.useEffect(() => {
    if (!eventData?.id || !isEventEnded(eventData) || !canResumeEvent(userId, eventData)) {
      return undefined;
    }
    let cancelled = false;
    fetchQueueSnapshot(eventData.id).then((items) => {
      if (!cancelled) setHistoryQueue(Array.isArray(items) ? items : []);
    });
    return () => {
      cancelled = true;
    };
  }, [eventData, userId]);

  React.useEffect(() => {
    if (!eventData || !isEventLive(eventData) || !isEventHost(userId, hostId)) return undefined;
    if (typeof reconcileHostParticipantDisplay === 'function') {
      Promise.resolve(reconcileHostParticipantDisplay()).catch(() => {});
    }
    return undefined;
  }, [eventData, userId, hostId]);

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 to-black text-white">
        <p>Reopening the party…</p>
      </div>
    );
  }

  // Not a member yet: join is an explicit act, never a side effect of the URL.
  if (status === RESUME_STATUS.notParticipant || status === RESUME_STATUS.notFound || status === RESUME_STATUS.left) {
    return <Navigate to={`/join/${routeCode}`} replace />;
  }

  if (status !== RESUME_STATUS.ok || !eventData) return <Navigate to="/" replace />;

  if (isEventEnded(eventData)) {
    // Recap is history for people who were in the room — never a public lookup
    // by event code. Join remains the unjoinable ended landing page.
    if (!canResumeEvent(userId, eventData)) {
      return <Navigate to={`/join/${routeCode}`} replace />;
    }
    const liveJustEnded = arrivedEndedRef.current.code === routeCode
      && arrivedEndedRef.current.ended === false;
    const recapQueue = historyQueue.length > 0 ? historyQueue : queue;
    return (
      <EndedSessionView
        eventData={eventData}
        queue={recapQueue}
        userId={userId}
        liveJustEnded={liveJustEnded}
        onHome={() => {
          cleanupStores();
          navigate('/');
        }}
      />
    );
  }

  const handleVideoSelect = (video) => {
    const songToAdd = {
      ...video,
      addedBy: userId,
      addedAt: new Date().toISOString(),
    };

    const added = addToQueue(songToAdd);
    if (added && !currentSong && isEventHost(userId, hostId)) {
      setCurrentSong(songToAdd, { reason: 'song' });
    }
  };

  const vibeParty = isVibeParty(eventData);
  const hostView = isEventHost(userId, hostId);

  return (
    <PartyVibeExperience
      eventId={eventData.id}
      userId={userId}
      isHost={hostView}
      enabled={vibeParty}
    >
      {(vibe) => {
        if (vibeParty && vibe.blocksParty) {
          return (
            <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-gray-900 to-black text-white">
              <div className="sticky top-0 z-20 bg-gradient-to-b">
                <EventHeader
                  arrivalInvite={arrivalInvite}
                  onOpenPanel={() => setSidePanelOpen(true)}
                  onEnded={() => navigate('/', { replace: true })}
                />
              </div>
              <main className="mx-auto w-full max-w-lg px-4 py-4 pb-28">
                <PartyVibeGate vibe={vibe} />
              </main>
              <SidePanel open={sidePanelOpen} onClose={() => setSidePanelOpen(false)} />
            </div>
          );
        }

        return (
          <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-gray-900 to-black text-white">
            <div className="md:[padding-right:var(--side-panel-width,20rem)]">
              <div className="sticky top-0 z-20 bg-gradient-to-b">
                <EventHeader
                  arrivalInvite={arrivalInvite}
                  onOpenPanel={() => setSidePanelOpen(true)}
                  onEnded={() => navigate('/', { replace: true })}
                />
              </div>

              <div className="container mx-auto p-4 pb-48">
                {vibeParty ? (
                  <div className="flex flex-col gap-8">
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      <SearchBar onResultSelect={handleVideoSelect} />
                      <ParticipantsList />
                    </div>
                    <div
                      data-testid="party-vibe-columns"
                      className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,11fr)_minmax(18rem,9fr)] lg:gap-10"
                    >
                      <div className="order-2 min-w-0 lg:order-1">
                        <PartyCrowdStage
                          vibe={vibe}
                          isHost={hostView}
                          playing={isPlaying}
                          songTitle={currentSong?.title || ''}
                        />
                      </div>
                      <div className="order-1 min-w-0 lg:order-2">
                        <PartyMyVibePanel
                          vibe={vibe}
                          eventId={eventData.id}
                          isHost={hostView}
                          vibeEnabled={vibeParty}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="min-w-0 space-y-6 lg:col-span-2">
                      <SearchBar onResultSelect={handleVideoSelect} />
                      <ParticipantsList />
                    </div>
                    <div className="min-w-0">
                      <LegacyPartyMood eventData={eventData} isPlaying={isPlaying} />
                    </div>
                  </div>
                )}
              </div>

              <div className="fixed bottom-0 left-0 right-0 z-10 md:[right:var(--side-panel-width,20rem)]">
                <Player />
                <FloatingDock />
              </div>
            </div>

            <SidePanel open={sidePanelOpen} onClose={() => setSidePanelOpen(false)} />
          </div>
        );
      }}
    </PartyVibeExperience>
  );
}
